import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCustomEventListener } from "react-custom-events";
import { toast } from "react-hot-toast";
import PaginationView from "../../components/molecules/Pagination";
import useQuery from "../../hooks/useQuery";
import { addIcon, defaultUserImage } from "../../assets/images";
import Brand from "../../api/Brand";
import { useTranslation } from "react-i18next";
import Loading from "../../components/Loading";
import { DashboardLayout } from "../../components/organisms/Layout";
import { encodeIds } from "../../utils";
import SearchBar from "../../components/atoms/SearchBar";
import Table from "../../components/atoms/Tables/Table";
import debounce from "lodash/debounce";

const Brands = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [metaData, setMetaData] = useState({});
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  // const [showAddModal, setShowAddModal] = useState(false);
  // const [showDeleteModal, setShowDeleteModal] = useState(false);
  // const [deleteLoading, setDeleteLoading] = useState(false);
  const [pageLimit, setPagelimit] = useState(50);
  const [searchBrand, setSearchBrand] = useState("");
  const [filteredBrands, setFilteredBrands] = useState([]);
  const query = useQuery();

  useEffect(() => {
    document.title = "HeyHire Portal-Brands";
  }, []);

  useEffect(() => {
    loadBrands();
  }, [query]);

  // useCustomEventListener("closeModal", () => {
  //   setShowDeleteModal(false);
  //   setShowAddModal(false);
  // });

  useCustomEventListener("brandAdded", () => {
    loadBrands();
    // setShowAddModal(false);
  });

  const debouncedUpdateSearch = useCallback(
    debounce((txt) => {
      // Create a new object from current search params
      const newParams = { ...Object.fromEntries(searchParams) };
      
      // Reset to page 1 when searching
      newParams.page = 1;
      
      // Add or remove search parameter based on value
      if (txt && txt.trim() !== '') {
        newParams.search = txt.trim();
      } else {
        delete newParams.search;
      }
      
      setSearchParams(newParams);
    }, 300),
    [searchParams]
  );

  const searchBrandName = (txt) => {
    setSearchBrand(txt);
    debouncedUpdateSearch(txt);
  };

  const loadBrands = () => {
    setLoading(true);
    const page = query.get("page") || 1;
    const limit = query.get("limit") || pageLimit;
    const search = query.get("search") || "";
    
    const pageData = page ? `page=${page}` : "";
    const searchData = search ? `&search=${encodeURIComponent(search)}` : "";
    const queryData = pageData + `&limit=${limit}` + searchData;
    
    // Update the search input field when loading from URL
    setSearchBrand(search);
    
    Brand.getAll(queryData)
      .then((response) => {
        setBrands(response.data.data);
        setMetaData(response.data.meta);
        setFilteredBrands(response.data.data);
      })
      .catch((error) => {
        if (error?.response?.status !== 403) {
          toast.error("Something went wrong. Please Try Again.");
        }
      })
      .then(() => setLoading(false));
  };

  const handlecPageChange = (currentPage, pageSize) => {
    setPagelimit(pageSize);
    // Create a new object from current search params
    const newParams = { ...Object.fromEntries(searchParams) };
    
    newParams.page = currentPage;
    newParams.limit = pageSize;
    
    // Only include search if it has a value
    if (searchBrand && searchBrand.trim() !== '') {
      newParams.search = searchBrand.trim();
    } else {
      delete newParams.search;
    }
    
    setSearchParams(newParams);
  };

  // const addBrand = (values) => {
  //   Brand.add(values)
  //     .then(() => {
  //       toast.success(t("brands.brand-added"));
  //       emitCustomEvent("brandAdded");
  //     })
  //     .catch((error) => {
  //       if (error.response && error.response.status === 422) {
  //         // Validation error
  //       } else {
  //         if (error?.response?.status !== 403) {
  //           toast.error(t("common.something-went-wrong-please-try-again"));
  //         }
  //       }
  //     });
  // };

  const deleteBrand = (brandId) => {
    // setDeleteLoading(true);
    Brand.deleteById(brandId)
      .then(() => {
        const dataCopy = brands.slice();
        dataCopy.splice(
          brands.findIndex((item) => item.id === brandId),
          1
        );
        setBrands(dataCopy);
        setFilteredBrands(dataCopy);
        // setShowDeleteModal(false);
        toast.success("Brand Deleted.");
      })
      .catch();
    // .then(() => setDeleteLoading(false));
  };
  return (
    <DashboardLayout darmo={true}>
      <div className="pb-12">
            <Table
              isLoading={loading}
              mainHeader="Brands"
              searchBar={
                <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full sm:justify-end">
                  <div className="w-full sm:w-[500px]">
                    <SearchBar
                      searchValue={searchBrand}
                      placeholder="Search by brand name..."
                      searchFunction={(value) => searchBrandName(value)}
                    />
                  </div>
                </div>
              }
              maxHeight="calc(100vh - 250px)"
              headers={[
                t("brands.brand-name"),
                t("common.company-name"),
                t("common.website"),
                "Edit",
                "Delete"
              ]}
              data={
                loading ? [] :
                filteredBrands?.map((brand, index) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <img
                            alt=""
                            src={
                              brand?.photo?.tiny_url ||
                              brand?.photo?.thumb_url ||
                              defaultUserImage
                            }
                            className="h-10 w-10 rounded-full ring-2 ring-gray-100"
                          />
                        </div>
                        <div className="ml-3">
                          <div className="font-semibold text-gray-900 hover:underline cursor-pointer">
                            {brand?.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      <div className="text-gray-700">
                        {brand?.company?.name}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      <div className="text-gray-700">
                        {brand?.website || "-"}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-2 sm:px-3 py-2.5 sm:py-3 text-sm md:text-base">
                      <div className="flex items-center gap-4">
                        <button
                          className="text-primary-600 hover:text-primary-900 font-medium hover:underline"
                          onClick={() =>
                            navigate(`/companies/brands/${encodeIds(brand?.id)}`)
                          }
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-2 sm:px-3 py-2.5 sm:py-3 text-sm md:text-base">
                      <div className="flex items-center gap-4">
                        <button
                          className="text-primary-600 hover:text-primary-900 font-medium hover:underline"
                          onClick={() => {
                            deleteBrand(brand?.id);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              }
              buttonData={
                <button
                  onClick={() => navigate("add")}
                  className="w-full sm:w-auto flex-shrink-0 cursor-pointer flex justify-center items-center rounded-lg bg-primary-500 px-6 h-10 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 transition-all duration-200"
                >
                  <img src={addIcon} alt="add" className="mr-2 w-4 h-4" />
                  Add Brand
                </button>
              }
            />
            <div className="flex w-full h-20 justify-center md:justify-end items-center px-4">
              {!loading && metaData?.total > 0 && (
                <PaginationView
                  current={metaData?.current_page}
                  next={metaData?.to}
                  pageSize={metaData?.per_page}
                  total={metaData?.total}
                  onChange={handlecPageChange}
                />
              )}
            </div>
      </div>
    </DashboardLayout>
  );
};

export default Brands;
